import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TenantData } from '../@core/interfaces/masters/tenant';
import { RolesData } from '../@core/interfaces/common/roles';
import { imageatch_detData } from '../@core/interfaces/masters/imageatch_det';
import { PatientRegistrationData } from '../@core/interfaces/patient-services/patient-registration';

export interface ResolvedTenant {
  tenantId: string;
  branchId: string;
  customerName: string;
  countryId: string;
  tenantType: string;
}

export interface RoleRedirectContext {
  tenantId: string;
  branchId: string;
  fullName: string;
  mobileCountryCode?: string;
}

// Care Navigator is an Employee_Mst-based role: any employee can authenticate via the
// employee login form, but only one whose JobCategoryId equals this value is actually a
// Care Navigator - see redirectEmployeeRole() below.
export const CARE_NAVIGATOR_JOB_CATEGORY_ID = 49;

// Adapted from CareLoginComponent's tenantCheck()/branchCheck()/getTenantBanner()/
// patientLoginCreate()/doctorLoginCreate() (care-login.component.ts) so the same
// tenant-resolution and role-based redirect behaviour can be reused without
// modifying CareLoginComponent itself.
@Injectable({ providedIn: 'root' })
export class TenantRoleLoginService {

  constructor(
    private tenantSrvc: TenantData,
    private rolesService: RolesData,
    private iatchSrvc: imageatch_detData,
    private patSrvc: PatientRegistrationData,
    private router: Router,
  ) { }

  resolveTenant(tenantAlias: string): Observable<ResolvedTenant> {
    const pay: any = { tenantId: tenantAlias, tenantEmailId: '' };
    return this.tenantSrvc.getTenantData(`${environment.apiUrl}/auth/validtenant`, pay).pipe(
      switchMap((data: any) => {
        const tenantId = data?.token?.id;
        const tenantType = data?.token?.tenantType;
        if (tenantId) {
          localStorage.setItem('tenantId', tenantId);
        }
        localStorage.setItem('tenantType', tenantType);

        return this.rolesService.list(`tenants/${tenantId}/tenantbranch`).pipe(
          map((branchData: any) => {
            const det = branchData?.value?.sort((a, b) =>
              new Date(b?.createdDate).getTime() - new Date(a?.createdDate).getTime())[0];
            const branchId = det?.id;
            // A tenant with no branches (or a malformed tenantbranch response) resolved
            // `branchId` to undefined here, but the localStorage write below happened
            // unconditionally - silently persisting an invalid branchId (read back later
            // as null/"undefined") instead of surfacing a failure. Error out instead, so
            // the caller's existing error handler shows a real message and no bad value
            // ever gets written.
            if (!tenantId || !branchId) {
              throw new Error('Unable to resolve a branch for this tenant.');
            }
            const customerName = det?.branchName;
            const countryId = det?.country;
            localStorage.setItem('customerName', customerName);
            localStorage.setItem('countryCode', det?.mobileCountryCode);
            localStorage.setItem('branchId', branchId);
            return { tenantId, branchId, customerName, countryId, tenantType };
          }),
        );
      }),
    );
  }

  getTenantBanner(tenantId: string, branchId: string): Observable<string | null> {
    return this.iatchSrvc.getById(`${environment.apiUrl}/ImageAtchDet/${tenantId}/${branchId}/loginBanner`).pipe(
      map((data: any) => {
        if (data?.length > 0) {
          const tenantLogo = data.filter((e: any) => e.type === 'logo');
          if (tenantLogo.length > 0) {
            const logo = 'data:image/jpeg;base64,' + tenantLogo[tenantLogo.length - 1].image;
            localStorage.setItem('customerTenantLogo', logo);
            return logo;
          }
        }
        return null;
      }),
    );
  }

  redirectAfterLogin(
    role: 'doctor' | 'patient' | 'care-navigator' | 'reviewer',
    ctx: RoleRedirectContext,
    onUnauthorized?: (message: string) => void,
  ): void {
    if (role === 'doctor') {
      this.redirectDoctor(onUnauthorized);
    } else if (role === 'care-navigator') {
      this.redirectEmployeeRole('100', 'Care Navigator', '/pages/well-spring/care-navigator-dashboard', {
        requiredJobCategoryId: CARE_NAVIGATOR_JOB_CATEGORY_ID,
        onUnauthorized,
      });
    } else if (role === 'reviewer') {
      this.redirectEmployeeRole('101', 'Peer Reviewer', '/pages/well-spring/reviewer-dashboard');
    } else {
      this.redirectPatient(ctx);
    }
  }

  private redirectPatient(ctx: RoleRedirectContext): void {
    const searchData = {
      searchWord: ctx.fullName,
      fromDate: null,
      toDate: null,
      skip: 0,
      take: 100,
    };
    this.patSrvc.searchList(`patientregistrationmst/${ctx.tenantId}/${ctx.branchId}/search`, searchData)
      .subscribe((data: any) => {
        const selected = data.find((x: any) => x.country === ctx.mobileCountryCode) || data[0];
        localStorage.setItem('regID', selected.id);
        localStorage.setItem('patientMobNo', ctx.fullName);
        localStorage.setItem('patientName', selected.firstname);
        localStorage.setItem('patientCountryCode', selected.country);
        localStorage.setItem('age', selected.age?.toString());
        localStorage.setItem('gender', selected?.gender);
        localStorage.setItem('dob', selected?.dob);
        localStorage.setItem('address1', selected?.address1 ? selected?.address1 : '');
        localStorage.setItem('address2', selected?.address2 ? selected?.address2 : '');
        localStorage.setItem('doorno', selected?.doorno ? selected?.doorno : '');
        localStorage.setItem('lastname', selected.lastname ? selected.lastname : '');
        localStorage.setItem('middlename', selected.middlename ? selected.middlename : '');
        if (data[0]?.email) {
          localStorage.setItem('patientEmail', data[0]?.email);
        }
        localStorage.setItem('InsuranceFlag', 'true');
        // Mirrors CareLoginComponent.patientLoginCreate()'s nphiesDiscoverIdentitySearch() call -
        // populates 'policyData' in localStorage the same way a legacy Care-Sphere login does,
        // so the merged well-spring-dashboard's insurance/plan-extension UI has real data to read.
        this.nphiesDiscoverIdentitySearch(ctx.tenantId, ctx.branchId, selected?.tourPassportNumber);
        const secondOpinionFlag = localStorage.getItem('SecondOpinionFlag') === 'true';
        this.router.navigate([secondOpinionFlag ? '/pages/well-spring/second-opinion-dashboard' : '/pages/well-spring/dashboard'], { queryParams: { fromLogin: true } });
      });
  }

  private nphiesDiscoverIdentitySearch(tenantId: string, branchId: string, passportNumber: string): void {
    if (!passportNumber) {
      return;
    }
    this.patSrvc.getById(`NphiesBeneficiaryDiscoveryInsurance/${tenantId}/${branchId}/GetNphiesBeneficiaryDiscoveryInsurances/PassportNumber/${passportNumber}`)
      .subscribe({
        next: (res: any) => {
          if (res) {
            localStorage.setItem('policyData', JSON.stringify(res));
          }
        },
        error: () => {},
      });
  }

  // Care Navigator (jobCategoryId 100 default label) / Reviewer (jobCategoryId 101 default
  // label) - both employee-type "Second Opinion" logins, following the same shape as
  // redirectDoctor()'s physician-by-id fetch, reusing the same
  // tenants/{t}/{b}/employee/{employeeId} endpoint NgxLoginComponent's main HMS employee login
  // already uses for its own jobCategoryId-based routing (which already returns jobCategoryId,
  // so no extra backend call is needed for the guard below). employeeId is set into
  // localStorage by InitUserService.initCurrentUser() when the backend's userFlag is 'Employee'.
  private redirectEmployeeRole(
    defaultJobCategoryId: string, defaultDesignation: string, dashboardRoute: string,
    guard?: { requiredJobCategoryId: number; onUnauthorized?: (message: string) => void },
  ): void {
    this.iatchSrvc.getById(`${environment.apiUrl}/tenants/${localStorage.getItem('tenantId')}/${localStorage.getItem('branchId')}/employee/${localStorage.getItem('employeeId')}`)
      .subscribe({
        next: (data: any) => {
          localStorage.setItem('restrictedViewPatientSurgery', String(data?.restrictedViewPatientSurgery ?? false));
          const jobCategoryId = data?.jobCategoryId;
          // The employee's credentials were valid, but they aren't actually a Care Navigator
          // (Employee_Mst.JobCategoryId doesn't match) - reject here rather than letting them
          // reach the Care Navigator dashboard under the wrong role.
          if (guard && Number(jobCategoryId) !== guard.requiredJobCategoryId) {
            guard.onUnauthorized?.(`${defaultDesignation} sign-in is not available for this account.`);
            return;
          }
          localStorage.setItem('jobCategoryId', String(jobCategoryId ?? defaultJobCategoryId));
          localStorage.setItem('employeeName', data?.fullName || `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim());
          localStorage.setItem('designation', data?.designation?.name ?? defaultDesignation);
          localStorage.setItem('InsuranceFlag', 'true');
          this.router.navigate([dashboardRoute]);
        },
        // The caller (patientportallogin.component.ts) keeps its Sign In button disabled
        // until this redirect resolves, to avoid a double-click firing a second concurrent
        // login/redirect chain - without this handler, a genuine failure here (as opposed to
        // the employeeId-race 404 this was written to catch) would error out silently and
        // leave that button stuck disabled with no way to retry.
        error: (err) => {
          console.error('Employee lookup failed during Second Opinion login redirect', err);
          guard?.onUnauthorized?.('Unable to verify your account. Please try again.');
        },
      });
  }

  private redirectDoctor(onUnauthorized?: (message: string) => void): void {
    const physicianId = localStorage.getItem('physicianId');
    if (!physicianId || physicianId === '0') {
      onUnauthorized?.('Doctor sign-in is not available for this account.');
      return;
    }
    this.iatchSrvc.getById(`${environment.apiUrl}/physicians/${localStorage.getItem('tenantId')}/${localStorage.getItem('branchId')}/${localStorage.getItem('physicianId')}/GetPhyscianById`)
      .subscribe({
        next: (data: any) => {
          localStorage.setItem('deptName', encodeURIComponent(data?.department?.name));
          localStorage.setItem('departmentId', data?.department?.id);
          localStorage.setItem('restrictedPatientAppointments', data?.restrictedPatientAppointments);
          localStorage.setItem('restrictedViewPatientSurgery', String(data?.restrictedViewPatientSurgery ?? false));
          localStorage.setItem('academicRank', data?.academicRank);
          localStorage.setItem('InsuranceFlag', 'true');
          // Only called by patientportallogin.component.ts's tenant-based WellSpring
          // Login flow (CareLoginComponent has its own separate doctorLoginCreate()) -
          // so a WellSpring doctor lands on WellSpring's own duplicated doctor
          const secondOpinionFlag = localStorage.getItem('SecondOpinionFlag') === 'true';
          this.router.navigate([secondOpinionFlag
            ? '/pages/well-spring/second-opinion-doctor-dashboard'
            : '/pages/well-spring/doctor-dashboard']);
        },
        error: (err) => {
          console.error('Physician lookup failed during WellSpring login redirect', err);
          onUnauthorized?.('Unable to verify your account. Please try again.');
        },
      });
  }
}
